<script setup>
import { ref, reactive, computed } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useVuelidate } from '@vuelidate/core';
import { required, email } from '@vuelidate/validators';
import Button from 'dashboard/components-next/button/Button.vue';
import { useArdhisasaTicketsStore } from 'dashboard/stores/ardhisasaTickets';
import * as ardhisasaLookups from 'dashboard/api/ardhisasaTickets';
import ardhisasaAgentsAPI from 'dashboard/api/ardhisasaAgents';
import ArdhisasaAuthModal from 'dashboard/routes/dashboard/tickets/components/ArdhisasaAuthModal.vue';

const emit = defineEmits(['close']);

const store = useStore();
const { t } = useI18n();
const ardhisasaStore = useArdhisasaTicketsStore();

const agentName = ref('');
const agentEmail = ref('');
const selectedRoleId = ref('agent');

const rules = {
  agentName: { required },
  agentEmail: { required, email },
  selectedRoleId: { required },
};

const v$ = useVuelidate(rules, {
  agentName,
  agentEmail,
  selectedRoleId,
});

const uiFlags = useMapGetter('agents/getUIFlags');
const getCustomRoles = useMapGetter('customRole/getCustomRoles');

const roles = computed(() => {
  const defaultRoles = [
    {
      id: 'administrator',
      name: 'administrator',
      label: t('AGENT_MGMT.AGENT_TYPES.ADMINISTRATOR'),
    },
    {
      id: 'agent',
      name: 'agent',
      label: t('AGENT_MGMT.AGENT_TYPES.AGENT'),
    },
  ];

  const customRoles = getCustomRoles.value.map(role => ({
    id: role.id,
    name: `custom_${role.id}`,
    label: role.name,
  }));

  return [...defaultRoles, ...customRoles];
});

const selectedRole = computed(() =>
  roles.value.find(
    role =>
      role.id === selectedRoleId.value || role.name === selectedRoleId.value
  )
);

// --- Ardhisasa linked-account section ---
const showArdhisasaSection = ref(false);
const showArdhisasaAuthModal = ref(false);
const ardhisasaLoading = reactive({
  defaults: false,
  counties: false,
  registries: false,
});
const ardhisasa = reactive({
  username: '',
  firstname: '',
  middlename: '',
  lastname: '',
  employeenum: '',
  phonenum: '',
  designationId: '',
  sectionId: '',
  countyQuery: '',
  countyId: '',
  registryOrStationId: '',
  registryOrStationType: '',
});
const departmentId = ref('');
const designationOptions = ref([]);
const sectionOptions = ref([]);
const countyResults = ref([]);
const registryOptions = ref([]);
const stationOptions = ref([]);

const ardhisasaValid = computed(() => {
  if (!showArdhisasaSection.value) return true;
  return !!(
    ardhisasa.username &&
    ardhisasa.firstname &&
    ardhisasa.lastname &&
    ardhisasa.employeenum &&
    ardhisasa.phonenum &&
    ardhisasa.designationId &&
    ardhisasa.countyId &&
    ardhisasa.registryOrStationId
  );
});

const extractList = data => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.details)) return data.details;
  if (Array.isArray(data?.results)) return data.results;
  return [];
};

const loadArdhisasaDefaults = async () => {
  if (!ardhisasaStore.isAuthenticated || departmentId.value) return;
  ardhisasaLoading.defaults = true;
  try {
    const { data } = await ardhisasaLookups.getCustomerCareDepartment(
      ardhisasaStore.token,
      ardhisasaStore.jwt
    );
    departmentId.value = data?.id || '';
    if (!departmentId.value) return;

    const [designationsRes, sectionsRes] = await Promise.all([
      ardhisasaLookups.getStaffDesignations(
        ardhisasaStore.token,
        ardhisasaStore.jwt,
        departmentId.value
      ),
      ardhisasaLookups.getSections(
        ardhisasaStore.token,
        ardhisasaStore.jwt,
        departmentId.value
      ),
    ]);
    designationOptions.value = extractList(designationsRes.data);
    sectionOptions.value = extractList(sectionsRes.data);
  } finally {
    ardhisasaLoading.defaults = false;
  }
};

const toggleArdhisasaSection = () => {
  showArdhisasaSection.value = !showArdhisasaSection.value;
  if (showArdhisasaSection.value && ardhisasaStore.isAuthenticated) {
    loadArdhisasaDefaults();
  }
};

let countySearchTimeout = null;
const onCountyInput = () => {
  clearTimeout(countySearchTimeout);
  countySearchTimeout = setTimeout(async () => {
    if (!ardhisasa.countyQuery) {
      countyResults.value = [];
      return;
    }
    ardhisasaLoading.counties = true;
    try {
      const { data } = await ardhisasaLookups.getCounties(
        ardhisasaStore.token,
        ardhisasaStore.jwt,
        ardhisasa.countyQuery
      );
      countyResults.value = extractList(data);
    } finally {
      ardhisasaLoading.counties = false;
    }
  }, 300);
};

const selectCounty = async county => {
  ardhisasa.countyId = county.id;
  ardhisasa.countyQuery = county.name;
  ardhisasa.registryOrStationId = '';
  ardhisasa.registryOrStationType = '';
  countyResults.value = [];
  ardhisasaLoading.registries = true;
  try {
    const { data } = await ardhisasaLookups.getRegistries(
      ardhisasaStore.token,
      ardhisasaStore.jwt,
      county.id
    );
    registryOptions.value = data?.registries || [];
    stationOptions.value = data?.stations || [];
  } finally {
    ardhisasaLoading.registries = false;
  }
};

const selectRegistryOrStation = (id, type) => {
  ardhisasa.registryOrStationId = id;
  ardhisasa.registryOrStationType = type;
};

const onArdhisasaAuthenticated = () => {
  showArdhisasaAuthModal.value = false;
  loadArdhisasaDefaults();
};

const extractDetailsMessage = details => {
  if (typeof details === 'string') return details;
  const firstField = Object.values(details || {})[0];
  return Array.isArray(firstField) ? firstField[0] : firstField;
};

const submittingArdhisasa = ref(false);

const addAgent = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  if (showArdhisasaSection.value && !ardhisasaValid.value) {
    useAlert(t('AGENT_MGMT.ADD.ARDHISASA.REQUIRED_FIELDS_ERROR'));
    return;
  }

  try {
    if (showArdhisasaSection.value) submittingArdhisasa.value = true;
    const payload = {
      name: agentName.value,
      email: agentEmail.value,
    };

    if (selectedRole.value.name.startsWith('custom_')) {
      payload.custom_role_id = selectedRole.value.id;
    } else {
      payload.role = selectedRole.value.name;
    }

    if (showArdhisasaSection.value) {
      await ardhisasaAgentsAPI.createWithArdhisasa(
        {
          ...payload,
          username: ardhisasa.username,
          firstname: ardhisasa.firstname,
          middlename: ardhisasa.middlename,
          lastname: ardhisasa.lastname,
          employeenum: ardhisasa.employeenum,
          phonenum: ardhisasa.phonenum,
          designation_id: ardhisasa.designationId,
          section_id: ardhisasa.sectionId || null,
          county: ardhisasa.countyId,
          registry:
            ardhisasa.registryOrStationType === 'registry'
              ? ardhisasa.registryOrStationId
              : null,
          station:
            ardhisasa.registryOrStationType === 'station'
              ? ardhisasa.registryOrStationId
              : null,
        },
        ardhisasaStore.token,
        ardhisasaStore.jwt
      );
      store.dispatch('agents/get');
      useAlert(t('AGENT_MGMT.ADD.ARDHISASA.SUCCESS_MESSAGE'));
    } else {
      await store.dispatch('agents/create', payload);
      useAlert(t('AGENT_MGMT.ADD.API.SUCCESS_MESSAGE'));
    }
    emit('close');
  } catch (error) {
    const {
      response: {
        data: {
          error: errorResponse = '',
          attributes: attributes = [],
          message: attrError = '',
          details,
        } = {},
      } = {},
    } = error;

    const detailsMessage = extractDetailsMessage(details);

    let errorMessage = showArdhisasaSection.value
      ? t('AGENT_MGMT.ADD.ARDHISASA.ERROR_MESSAGE')
      : t('AGENT_MGMT.ADD.API.ERROR_MESSAGE');
    if (
      !showArdhisasaSection.value &&
      error?.response?.status === 422 &&
      !attributes.includes('base')
    ) {
      errorMessage = t('AGENT_MGMT.ADD.API.EXIST_MESSAGE');
    }
    useAlert(errorResponse || attrError || detailsMessage || errorMessage);
  } finally {
    submittingArdhisasa.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header
      :header-title="$t('AGENT_MGMT.ADD.TITLE')"
      :header-content="$t('AGENT_MGMT.ADD.DESC')"
    />
    <form class="flex flex-col items-start w-full" @submit.prevent="addAgent">
      <div class="w-full">
        <label :class="{ error: v$.agentName.$error }">
          {{ $t('AGENT_MGMT.ADD.FORM.NAME.LABEL') }}
          <input
            v-model="agentName"
            type="text"
            :placeholder="$t('AGENT_MGMT.ADD.FORM.NAME.PLACEHOLDER')"
            @input="v$.agentName.$touch"
          />
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.selectedRoleId.$error }">
          {{ $t('AGENT_MGMT.ADD.FORM.AGENT_TYPE.LABEL') }}
          <select v-model="selectedRoleId" @change="v$.selectedRoleId.$touch">
            <option v-for="role in roles" :key="role.id" :value="role.id">
              {{ role.label }}
            </option>
          </select>
          <span v-if="v$.selectedRoleId.$error" class="message">
            {{ $t('AGENT_MGMT.ADD.FORM.AGENT_TYPE.ERROR') }}
          </span>
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.agentEmail.$error }">
          {{ $t('AGENT_MGMT.ADD.FORM.EMAIL.LABEL') }}
          <input
            v-model="agentEmail"
            type="email"
            :placeholder="$t('AGENT_MGMT.ADD.FORM.EMAIL.PLACEHOLDER')"
            @input="v$.agentEmail.$touch"
          />
        </label>
      </div>

      <div class="w-full pt-2 mt-2 border-t border-n-weak">
        <button
          type="button"
          class="flex items-center gap-2 text-sm font-medium text-n-slate-11"
          @click="toggleArdhisasaSection"
        >
          <input type="checkbox" :checked="showArdhisasaSection" />
          {{ $t('AGENT_MGMT.ADD.ARDHISASA.TOGGLE_LABEL') }}
        </button>

        <div
          v-if="showArdhisasaSection"
          class="flex flex-col w-full gap-3 mt-3"
        >
          <div
            v-if="!ardhisasaStore.isAuthenticated"
            class="flex items-center justify-between p-3 text-sm rounded-lg bg-n-alpha-2"
          >
            <span>{{ $t('AGENT_MGMT.ADD.ARDHISASA.LOGIN_REQUIRED') }}</span>
            <Button
              faded
              slate
              type="button"
              size="small"
              :label="$t('AGENT_MGMT.ADD.ARDHISASA.LOGIN_BUTTON')"
              @click="showArdhisasaAuthModal = true"
            />
          </div>

          <template v-else>
            <div class="w-full">
              <label>
                {{ $t('AGENT_MGMT.ADD.ARDHISASA.USERNAME.LABEL') }}
                <input v-model="ardhisasa.username" type="text" />
              </label>
            </div>
            <div class="grid grid-cols-3 gap-2">
              <label>
                {{ $t('AGENT_MGMT.ADD.ARDHISASA.FIRSTNAME.LABEL') }}
                <input v-model="ardhisasa.firstname" type="text" />
              </label>
              <label>
                {{ $t('AGENT_MGMT.ADD.ARDHISASA.MIDDLENAME.LABEL') }}
                <input v-model="ardhisasa.middlename" type="text" />
              </label>
              <label>
                {{ $t('AGENT_MGMT.ADD.ARDHISASA.LASTNAME.LABEL') }}
                <input v-model="ardhisasa.lastname" type="text" />
              </label>
            </div>
            <div class="grid grid-cols-2 gap-2">
              <label>
                {{ $t('AGENT_MGMT.ADD.ARDHISASA.EMPLOYEE_NUMBER.LABEL') }}
                <input v-model="ardhisasa.employeenum" type="text" />
              </label>
              <label>
                {{ $t('AGENT_MGMT.ADD.ARDHISASA.PHONE_NUMBER.LABEL') }}
                <input v-model="ardhisasa.phonenum" type="text" />
              </label>
            </div>
            <div class="grid grid-cols-2 gap-2">
              <label>
                {{ $t('AGENT_MGMT.ADD.ARDHISASA.DESIGNATION.LABEL') }}
                <select
                  v-model="ardhisasa.designationId"
                  :disabled="ardhisasaLoading.defaults"
                >
                  <option value="" disabled>
                    {{ $t('AGENT_MGMT.ADD.ARDHISASA.DESIGNATION.PLACEHOLDER') }}
                  </option>
                  <option
                    v-for="d in designationOptions"
                    :key="d.id"
                    :value="d.id"
                  >
                    {{ d.name }}
                  </option>
                </select>
              </label>
              <label>
                {{ $t('AGENT_MGMT.ADD.ARDHISASA.SECTION.LABEL') }}
                <select
                  v-model="ardhisasa.sectionId"
                  :disabled="ardhisasaLoading.defaults"
                >
                  <option value="">
                    {{ $t('AGENT_MGMT.ADD.ARDHISASA.SECTION.PLACEHOLDER') }}
                  </option>
                  <option v-for="s in sectionOptions" :key="s.id" :value="s.id">
                    {{ s.name }}
                  </option>
                </select>
              </label>
            </div>
            <div class="relative w-full">
              <label>
                {{ $t('AGENT_MGMT.ADD.ARDHISASA.COUNTY.LABEL') }}
                <input
                  v-model="ardhisasa.countyQuery"
                  type="text"
                  :placeholder="
                    $t('AGENT_MGMT.ADD.ARDHISASA.COUNTY.PLACEHOLDER')
                  "
                  @input="onCountyInput"
                />
              </label>
              <ul
                v-if="countyResults.length"
                class="absolute z-10 w-full mt-1 overflow-auto bg-white border rounded-lg shadow-lg border-n-weak max-h-40 dark:bg-n-solid-2"
              >
                <li
                  v-for="county in countyResults"
                  :key="county.id"
                  class="px-3 py-2 text-sm cursor-pointer hover:bg-n-alpha-2"
                  @click="selectCounty(county)"
                >
                  {{ county.name }}
                </li>
              </ul>
            </div>
            <div
              v-if="registryOptions.length || stationOptions.length"
              class="w-full"
            >
              <p class="mb-1 text-sm font-medium text-n-slate-11">
                {{ $t('AGENT_MGMT.ADD.ARDHISASA.REGISTRY_OR_STATION.LABEL') }}
              </p>
              <div class="flex flex-wrap gap-2">
                <label
                  v-for="registry in registryOptions"
                  :key="`registry-${registry.id}`"
                  class="flex items-center gap-1 px-2 py-1 text-sm border rounded-lg border-n-weak"
                >
                  <input
                    type="radio"
                    name="registryOrStation"
                    :checked="ardhisasa.registryOrStationId === registry.id"
                    @change="selectRegistryOrStation(registry.id, 'registry')"
                  />
                  {{ registry.name }}
                </label>
                <label
                  v-for="station in stationOptions"
                  :key="`station-${station.id}`"
                  class="flex items-center gap-1 px-2 py-1 text-sm border rounded-lg border-n-weak"
                >
                  <input
                    type="radio"
                    name="registryOrStation"
                    :checked="ardhisasa.registryOrStationId === station.id"
                    @change="selectRegistryOrStation(station.id, 'station')"
                  />
                  {{ station.name }}
                </label>
              </div>
            </div>
          </template>
        </div>
      </div>

      <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
        <Button
          faded
          slate
          type="reset"
          :label="$t('AGENT_MGMT.ADD.CANCEL_BUTTON_TEXT')"
          @click.prevent="emit('close')"
        />
        <Button
          type="submit"
          :label="$t('AGENT_MGMT.ADD.FORM.SUBMIT')"
          :disabled="v$.$invalid || uiFlags.isCreating || submittingArdhisasa"
          :is-loading="uiFlags.isCreating || submittingArdhisasa"
        />
      </div>
    </form>

    <ArdhisasaAuthModal
      v-if="showArdhisasaAuthModal"
      @authenticated="onArdhisasaAuthenticated"
      @close="showArdhisasaAuthModal = false"
    />
  </div>
</template>
